import { Avatar, Box, Typography } from '@mui/joy';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import { FC, JSX, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { AlertModal } from '../AlertModal/AlertModal';
import { useTranslation } from 'react-i18next';

type LogoutBtnProps = {
  variant?: 'plain' | 'outlined' | 'soft' | 'solid';
  onClick?: () => void;
};

export const LogoutBtnBase: FC<LogoutBtnProps> = ({
  variant = 'plain',
  onClick,
}): JSX.Element => {
  const { t } = useTranslation();
  const auth = useAuth();
  const [openLogoutModal, setOpenLogoutModal] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);

      // Call the onClick prop if provided (contains the proper signoutRedirect logic)
      if (onClick) {
        await onClick();
      } else {
        // Fallback to removeUser if no onClick prop is provided
        await auth.removeUser();
      }

      // Clear local and session storage
      // This is a workaround for the issue where localStorage and sessionStorage are not cleared in some browsers
      // @ts-ignore
      localStorage.clear();
      // @ts-ignore
      sessionStorage.clear();
    } catch (error) {
      console.error('Logout failed', error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  return (
    <>
      <AlertModal
        alertAction={handleLogout}
        alertActionText={t('Logout')}
        alertCancelText={t('Cancel')}
        alertMessage={t('Are you sure you want to logout?')}
        alertTitle={t('Logout Confirmation')}
        isLoading={isLoggingOut}
        open={openLogoutModal}
        setOpen={setOpenLogoutModal}
      />
      <Box
        onClick={(e) => {
          e.stopPropagation();
          setOpenLogoutModal(true);
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          py: 0.75,
          width: '100%',
        }}
      >
        <Avatar
          variant="outlined"
          size="sm"
          sx={{
            '--Avatar-size': '14',
            bgcolor: 'transparent',
            color: 'text.secondary',
            p: 0.5,
          }}
        >
          <LogoutRoundedIcon fontSize="small" sx={{ fontSize: '14px' }} />
        </Avatar>
        <Box onClick={() => setOpenLogoutModal(true)}>
          <Typography level="body-sm" sx={{ fontWeight: 500 }}>
            {t('Logout')}
          </Typography>
        </Box>
      </Box>
    </>
  );
};
